/**
 * クエリ登録画面の「フィルタ対象カラム」行の状態をまとめて扱うフック。
 *
 * 行の追加・削除だけでなく、「DB内カラムから選択」で必要になる参照テーブル一覧と
 * テーブルごとのカラム一覧の取得も持つ。登録画面本体に置くと編集中の SQL・DB 選択と
 * 絡んだ state が増えて読みにくくなるため、ここへ寄せている。
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business 登録画面のフィルタ対象カラム行と、DB のテーブル / カラム候補の取得をまとめて管理する
 * @graph-connects usePreviewQueryTables [calls] 参照テーブル一覧取得
 * @graph-connects usePreviewTableColumns [calls] テーブルのカラム一覧取得
 */
import { useState } from 'react';
import { usePreviewQueryTables, usePreviewTableColumns } from '../../../../hooks/useDataViewer.js';
import {
  createEmptyDatabaseColumnRow,
  createEmptyFilterableColumnRow,
  type FilterableColumnRow,
} from './FilterableColumnsEditor.js';
import type { DataViewerTable, DataViewerTableColumn } from '../../../../types/api.js';

/** {@link useFilterableColumnRows} の入力。 */
export interface UseFilterableColumnRowsOptions {
  /** 編集中の SQL。DB 内カラムの候補はこの SQL の参照テーブルから引く */
  sql: string;
  /** 編集中の接続先 DB */
  datasetId: string;
  /** 候補取得に失敗したときの通知先 */
  onError: (message: string) => void;
}

/**
 * フィルタ対象カラム行の state と操作をまとめて返す。
 *
 * @param options 編集中の SQL / DB とエラー通知先
 * @returns 行一覧・候補データ・行操作ハンドラ
 * @graph-business 登録画面のフィルタ対象カラム行を管理し、DB の候補取得を仲介する
 * @graph-connects none
 */
export function useFilterableColumnRows({
  sql,
  datasetId,
  onError,
}: UseFilterableColumnRowsOptions) {
  const [rows, setRows] = useState<FilterableColumnRow[]>([]);
  const [tables, setTables] = useState<DataViewerTable[]>([]);
  const [columnsByTable, setColumnsByTable] = useState<Record<string, DataViewerTableColumn[]>>({});
  const [loadingColumnsTable, setLoadingColumnsTable] = useState<string | null>(null);
  const previewQueryTables = usePreviewQueryTables();
  const previewTableColumns = usePreviewTableColumns();

  const addRow = (): void => {
    setRows((prev) => [...prev, createEmptyFilterableColumnRow()]);
  };

  /**
   * 「DB内カラムから選択」の行を足す。
   *
   * 押した時点の SQL が参照しているテーブルを取り直す。SQL は編集中に変わるので、
   * 前回取得した候補をそのまま出すと古いテーブルを選ばせてしまう。
   */
  const addDatabaseRow = (): void => {
    setRows((prev) => [...prev, createEmptyDatabaseColumnRow()]);
    if (sql === '' || datasetId === '') return;
    previewQueryTables.mutate(
      { sql, datasetId },
      {
        onSuccess: (result) => {
          setTables(result.tables);
        },
        onError: (error) => {
          onError(error.message);
        },
      },
    );
  };

  const removeRow = (rowId: string): void => {
    setRows((prev) => prev.filter((row) => row.id !== rowId));
  };

  const updateColumnName = (rowId: string, column: string): void => {
    // カラム名を変更したら、旧名に紐づく保存済み型表示は無効になるため未判定に戻す
    // （実際に保存される型は送信時に実データから自動判定される）。
    setRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, column, type: null } : row)));
  };

  /** テーブルを選び直したら、そのテーブルのカラム一覧を取りに行く。カラム選択はリセットする。 */
  const updateTable = (rowId: string, table: string): void => {
    setRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, table, column: '', type: null } : row)),
    );
    if (table === '' || columnsByTable[table] !== undefined) return;

    setLoadingColumnsTable(table);
    previewTableColumns.mutate(
      { sql, datasetId, table },
      {
        onSuccess: (result) => {
          setColumnsByTable((prev) => ({ ...prev, [table]: result.columns }));
          setLoadingColumnsTable(null);
        },
        onError: (error) => {
          setLoadingColumnsTable(null);
          onError(error.message);
        },
      },
    );
  };

  /** カラムを選んだら、DB の型表記から判定済みの型をそのまま行に載せる（送信時の型推論は不要）。 */
  const updateDatabaseColumn = (rowId: string, column: string): void => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;
        const candidates = row.table === null ? [] : (columnsByTable[row.table] ?? []);
        const selected = candidates.find((candidate) => candidate.field === column);
        return { ...row, column, type: selected?.filterType ?? null };
      }),
    );
  };

  return {
    rows,
    setRows,
    tables,
    columnsByTable,
    isLoadingTables: previewQueryTables.isPending,
    loadingColumnsTable,
    addRow,
    addDatabaseRow,
    removeRow,
    updateColumnName,
    updateTable,
    updateDatabaseColumn,
  };
}
